import type { Breakdown, BreakdownRow, CellNote } from '@dashboard/contract';
import { CLASS_LABEL, fmtM, fmtPct, growthText, monthLabel } from '../lib/format';

function noteText(n: CellNote): string {
  const parts = [`${fmtM(n.from)}M → ${fmtM(n.to)}M`];
  if (n.releaseTiming === 'inside_window') parts.push('released inside this window — partial year');
  if (n.releaseTiming === 'first_full_year') parts.push('first full year after release');
  for (const e of n.events) {
    parts.push(
      `${monthLabel(e.month)} ${(CLASS_LABEL[e.class] ?? e.class).toLowerCase()}: ${e.trackTitle} +${(e.dev * 100).toFixed(0)}%${e.cause ? ` — ${e.cause}` : ''}`,
    );
  }
  if (parts.length === 1) parts.push('no release-timing or event cause in the data');
  return parts.join(' · ');
}

function NameCell({ r }: { r: BreakdownRow }) {
  if (r.kind === 'aggregate') {
    return (
      <td className="name" title={r.label}>
        {r.label}
      </td>
    );
  }
  return (
    <td className="name" title={`${r.label} · ${r.releaseDate ?? 'no release date'}`}>
      <i className={`dot ${{ catalogue: 'c', new: 'n', unknown: 'u' }[r.vintage ?? 'unknown']}`} />
      {r.label}
      {r.ovr ? (
        <span className="ovr" title="researched commercial release date">
          ◆
        </span>
      ) : null}
    </td>
  );
}

export function GrowthTable({ b }: { b: Breakdown }) {
  const yoy = b.growthColumns.filter((c) => c.kind === 'yoy');
  const compound = b.growthColumns.filter((c) => c.kind === 'compound');
  const row = (r: BreakdownRow) => {
    const cls =
      r.kind === 'aggregate'
        ? {
            topN: 'sub',
            other: 'split',
            total: 'grand',
            catalogue: 'split',
            new: 'split',
            unknown: 'split',
          }[r.key]
        : '';
    return (
      <tr key={r.key} className={cls}>
        <NameCell r={r} />
        <td className="rel">{r.kind === 'aggregate' ? (r.note ?? '') : (r.releaseYear ?? '—')}</td>
        <td className="pct">{fmtPct(r.shareOfBasis)}</td>
        {yoy.map((c) => {
          const g = growthText(r.growth[c.key]);
          const flag = r.flags[c.key];
          return (
            <td
              key={c.key}
              className={flag ? `big50${(r.growth[c.key] ?? 0) < 0 ? ' dn' : ''}` : ''}
              title={flag ? noteText(flag) : undefined}
            >
              <span className={g.cls}>{g.text}</span>
            </td>
          );
        })}
        {compound.map((c) => {
          const g = growthText(r.growth[c.key]);
          return (
            <td key={c.key}>
              <span className={g.cls}>{g.text}</span>
            </td>
          );
        })}
      </tr>
    );
  };
  const tracks = b.rows.filter((r) => r.kind === 'track');
  const aggs = b.rows.filter((r) => r.kind === 'aggregate');
  return (
    <div className="scroll">
      <table id="t-growth">
        <thead>
          <tr>
            <th className="name">Song Name</th>
            <th>Release</th>
            <th>% Total</th>
            {yoy.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
            {compound.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tracks.map(row)}
          {aggs.map((a, i) => (
            <Fragment key={a.key}>
              {i === 2 ? (
                <tr className="gap">
                  <td className="name" />
                  <td colSpan={yoy.length + compound.length + 2} />
                </tr>
              ) : null}
              {row(a)}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

import { Fragment } from 'react';
